import styled, { keyframes } from 'styled-components';
import earthBird from 'assets/img/illustrations/earth.png';
import marsBird from 'assets/img/illustrations/mars.png';
import Container from 'components/Container';
import FadeInImage from 'components/FadeInImage';
import {
  display1Style,
  display3Style,
  paragraphLStyle,
  titleXlStyle,
  heading3Style,
  paragraphSStyle,
  AccentText
} from 'components/Gen.2/Typography';
import { colors } from 'style/theme';
import { sizes } from 'style';

export const Background = styled.div`
  position: relative;
  background-color: ${colors.minsk};
  min-height: 50svh;

  @media ${sizes.desktop} {
    // Let parallax image-canvas show on desktop
    background-color: transparent;
  }
`;

export const StarsContainer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

export const HeroContainer = styled(Container)`
  min-height: initial;
  display: grid;
  gap: 24px;
  align-items: start;
  justify-items: start;
  padding-top: 96px;
  padding-bottom: 24px;
  z-index: 1;

  grid-template-columns: 3fr 1fr;
  grid-template-areas:
    'heading heading'
    'pricing pricing'
    'cta cta'
    'bird2 bird1';

  @media ${sizes.tabletPortrait} {
    grid-template-areas:
      'heading heading'
      'pricing pricing'
      'cta bird1'
      'bird2 bird1';
    padding-top: 160px;
    padding-bottom: 40px;
    gap: 32px;
  }

  @media ${sizes.largeDesktop} {
    padding-top: 180px;
    min-height: 740px;
    grid-template-columns: repeat(3, 1fr);
    grid-template-areas:
      'heading heading bird1'
      'pricing pricing bird1'
      'cta bird2 bird1';
  }

  @media ${sizes.monitor} {
    padding-top: 220px;
  }
`;

export const Heading = styled.h1`
  grid-area: heading;
  display: flex;
  flex-direction: column;

  // TitleXl on mobile, Display1 on tabletPortrait
  ${titleXlStyle}
  @media ${sizes.tabletPortrait} {
    ${display1Style}
  }
`;

export const Pricing = styled.div`
  grid-area: pricing;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Price = styled.h2`
  ${titleXlStyle}
  @media ${sizes.tabletPortrait} {
    ${display3Style};
  }
`;

export const PriceSub = styled.span`
  margin-left: 4px;
  ${paragraphLStyle};
  @media ${sizes.tabletPortrait} {
    ${heading3Style};
  }
`;

export const Cta = styled.div`
  grid-area: cta;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: start;
  padding-bottom: 96px;
  @media ${sizes.tabletPortrait} {
    padding-bottom: 0;
  }
`;

export const CtaRow = styled.div`
  grid-area: cta;
  display: flex;
  flex-direction: row;
  gap: 24px;
  width: 100%;
  padding-bottom: 96px;

  & > * {
    flex: 1;
  }

  @media ${sizes.tabletPortrait} {
    padding-bottom: 0;
    & > * {
      flex: initial;
    }
  }

  @media ${sizes.tabletLandscape} {
    width: initial;
  }
`;

export const SecondaryCta = styled.p`
  ${paragraphSStyle};

  & > button {
    ${paragraphSStyle};
    display: inline;
    background-color: transparent;
    border: none;
    padding: 0;
    margin: 0;
    cursor: pointer;
    color: inherit;
    text-decoration: underline;
    text-align: start;
  }
`;

export const MarsBird = styled(FadeInImage).attrs({
  src: marsBird,
  alt: 'bird floating in space'
})`
  grid-area: bird1;

  display: none;
  width: min(40vw, 365px);
  max-width: none;
  justify-self: center;
  transform: translateY(-15vw);
  pointer-events: none;
  user-select: none;

  @media ${sizes.tabletLandscape} {
    display: block;
  }

  @media ${sizes.largeDesktop} {
    transform: none;
  }
`;

export const EarthBird = styled(FadeInImage).attrs({
  src: earthBird,
  alt: 'bird floating in space'
})`
  grid-area: bird1;

  display: none;
  width: 166px;
  max-width: none;
  align-self: center;
  justify-self: center;
  pointer-events: none;
  user-select: none;
  animation-name: ${keyframes`
    0% {
        transform: translate3d(0, 0, 0);
    }
    100% {
      transform: translate3d(0, 6%, 0);
    }
  `};
  animation-duration: 1.5s;
  animation-iteration-count: infinite;
  animation-direction: alternate;

  @media ${sizes.tabletPortrait} {
    display: block;
  }

  @media ${sizes.tabletLandscape} {
    grid-area: bird2;
    width: 257px;
    animation-name: ${keyframes`
      0% {
          transform: translate3d(0, 0, 0);
      }
      100% {
        transform: translate3d(0, -6%, 0);
      }
    `};
  }
`;

export const Em = styled(AccentText).attrs({ colorName: 'bermudaGreen', as: 'em' })`
  display: block;
  font-style: normal;
`;
